
import { areShallowEqual } from '@ms/utilities-objects/lib/areShallowEqual';

export { assign } from '@ms/utilities-objects/lib/assign';

export type IRemap<TFromValue, TObject extends {
  [key: string]: TFromValue;
}, TToValue> = {
    [K in keyof TObject]: TToValue;
  };

/**
 * Produces a new object with each key and value in the original object
 * mapped to a new key and value.
 * @public
 */
export function mapObject<TFromValue, TObject extends {
  [key: string]: TFromValue;
}, TToValue, TToKey extends (string | number | symbol) = keyof TObject>(
  input: TObject,
  select: (value: TFromValue, key: keyof TObject) => TToValue,
  mapKey: (key: keyof TObject, value: TFromValue) => TToKey | undefined): {
    [K in TToKey]: TToValue;
  };
/**
 * Produces a new object with each value in the original mapped
 * to a new value.
 * @public
 */
export function mapObject<TFromValue, TObject extends {
  [key: string]: TFromValue;
}, TToValue>(
  input: TObject,
  select: (value: TFromValue, key: keyof TObject) => TToValue): IRemap<TFromValue, TObject, TToValue>;
/**
 * Implementation.
 * @internal
 */
export function mapObject<TFromValue, TObject extends {
  [key: string]: TFromValue;
}, TToValue>(
  input: TObject,
  select: (value: TFromValue, key: keyof TObject) => TToValue,
  mapKey?: (key: keyof TObject, newValue: TFromValue) => string | undefined): IRemap<TFromValue, TObject, TToValue> {
  const result: Partial<IRemap<TFromValue, TObject, TToValue>> = {};

  for (const key in input) {
    if (Object.prototype.hasOwnProperty.call(input, key)) {
      const value = input[key];

      const mappedKey = mapKey ? mapKey(key, value as {} as TFromValue) : key;

      if (typeof mappedKey === 'string') {
        result[mappedKey] = select(value as {} as TFromValue, mappedKey);
      }
    }
  }

  return <IRemap<TFromValue, TObject, TToValue>>result;
}

/**
 * Picks a subset of keys from a source object.
 * The target keys are specified using an array.
 * @public
 */
export function pickObject<TToObject, TFromKey extends keyof TToObject>(
  input: TToObject,
  keys: TFromKey[]
): {
    [K in TFromKey]: TToObject[K];
  };
/**
 * Picks a subset of keys from a source object.
 * The target keys are specified using an object.
 * @public
 */
export function pickObject<TToObject, TFromKey extends keyof TToObject, D = {}>(
  input: TToObject,
  objectWithKeys: { [K in TFromKey]: D; }
): {
    [K in TFromKey]: TToObject[K];
  };
/**
 * Picks a subset of keys from a source object.
 * The key subset can either be an array or an object with the key subset.
 * @internal
 */
export function pickObject<TToObject, TFromKey extends keyof TToObject>(
  input: TToObject,
  keysOrObjectWithKeys: TFromKey[] | {
    [K in TFromKey]: {};
  }
): {
    [K in TFromKey]: TToObject[K];
  } {
  const keys = keysOrObjectWithKeys instanceof Array ? makeObject(keysOrObjectWithKeys, () => true) : keysOrObjectWithKeys;

  let newValue: (typeof input) | undefined = undefined;

  for (const key in input) {
    if (Object.prototype.hasOwnProperty.call(input, key)) {
      if (key in keys) {
        // Do nothing.
      } else {
        if (!newValue) {
          newValue = {
            ...(input as {})
          } as typeof input;
        }

        delete newValue[key];
      }
    }
  }

  /* tslint:disable-next-line:no-any */
  return (newValue || input) as any;
}

/**
 * Makes an object where each value in an input array is a key
 * and a given function is used to determine the value for each.
 * @public
 */
export function makeObject<K extends (string | number | symbol), F, T>(
  inputs: T[],
  getValue: (input: T) => F,
  getKey: (input: T) => K
): {
    [P in K]: F;
  };
/**
 * Makes an object where each value in an input array is a key
 * and a given function is used to determine the value for each.
 * @public
 */
export function makeObject<K extends (string | number | symbol), F>(keys: K[], getValue: (key: K) => F): {
  [P in K]: F;
};
/**
 * Makes an object where each value in an input array is a key
 * and a given function is used to determine the value for each.
 * @internal
 */
export function makeObject<K extends (string | number | symbol), F, T = K>(
  inputsOrKeys: K[] | T[],
  getValue: (key: K) => F,
  getKey?: (input: T) => K
): {
    [P in K]: F;
  } {
  const result: Partial<{
    [P in K]: F;
  }> = {};

  for (const inputOrKey of inputsOrKeys) {
    const key: K = getKey ? getKey(inputOrKey as T) : inputOrKey as K;

    result[key] = getValue(inputOrKey as K);
  }

  return <{
    [P in K]: F;
  }>result;
}

export { areShallowEqual };
